/**
 * subscriptionService.js
 * Pure-logic layer for fetching and resolving subscription bundles.
 * Extracted from AccountSubscription/index.js so that SubscriptionContext
 * and LocationModal can reuse the same logic without a React dependency.
 */

import {
  fetchActiveSubscription,
  fetchActiveAddons,
  fetchAddonDetails,
  fetchUpcomingBilling,
  fetchAccountByOrg,
} from "../config/Services";

/* ──────────────────────────────────────────────────────────
   Internal helpers (mirrored from AccountSubscription)
   ────────────────────────────────────────────────────────── */

/** Resolve the name of an addon from catalog detail */
function addonName(detail) {
  if (!detail) return null;
  return detail.packageName || detail.addonName || detail.name || detail.title || null;
}

/** Walk all response envelope variants for addon details → array */
function extractAddonArray(responseData) {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.data)) return responseData.data;
  if (responseData.data && Array.isArray(responseData.data.data)) return responseData.data.data;
  if (responseData.data && Array.isArray(responseData.data.addons)) return responseData.data.addons;
  return [];
}

/* ──────────────────────────────────────────────────────────
   fetchSubscriptionBundle — 3-step async flow
   ────────────────────────────────────────────────────────── */

/**
 * Fetch the full subscription bundle for a given account/org/location.
 * All network failures are non-fatal; a `fetchFailed: true` flag is set
 * when the primary subscription fetch itself fails so callers can
 * decide whether to allow optimistic pass-through.
 *
 * Returns:
 *   { subscription, upcomingBilling, addons, fetchedAt, subscriptionMessage, fetchFailed }
 */
export async function fetchSubscriptionBundle(accountId, orgId, locId) {
  let fetchFailed = false;

  /* Step 0 — resolve real AC* accountId if not already in localStorage */
  let resolvedAccountId = accountId;
  if (!resolvedAccountId && orgId) {
    try {
      const r = await fetchAccountByOrg(orgId);
      const aid = r?.data?.data?.data?.accountId;
      if (aid) {
        resolvedAccountId = aid;
        localStorage.setItem("accountId", aid);
      }
    } catch (_) {
      /* non-fatal */
    }
  }
  const effectiveAccountId = resolvedAccountId;

  /* Step 1 — parallel: active subscription + active addons */
  const [subRes, addonsRes] = await Promise.allSettled([
    fetchActiveSubscription({ orgId, locId, accountId: effectiveAccountId }),
    fetchActiveAddons({ accountId: effectiveAccountId, orgId, locationId: locId }),
  ]);

  /* Parse subscription */
  let subscription = null;
  let subscriptionMessage = null;
  if (subRes.status === "fulfilled") {
    const d = subRes.value?.data;
    subscriptionMessage = d?.data?.message || null;
    const arr = d?.data?.data;
    if (Array.isArray(arr) && arr.length > 0) {
      subscription =
        arr.find((x) => x?.status === "ACTIVE" || x?.status === "TRIAL_ACTIVE") || arr[0];
    }
  } else {
    fetchFailed = true;
  }

  /* Parse raw addons */
  let rawAddons = [];
  if (addonsRes.status === "fulfilled") {
    const arr = addonsRes.value?.data?.data?.data;
    if (Array.isArray(arr)) rawAddons = arr;
  }

  /* Step 2 — parallel: addon catalog + upcoming billing */
  const addonIds = rawAddons.map((a) => a?.addonId).filter(Boolean);
  const hasSubscriptionId = !!subscription?.subscriptionId;

  const [catalogRes, billingRes] = await Promise.allSettled([
    addonIds.length > 0 ? fetchAddonDetails({ addonIds }) : Promise.resolve(null),
    hasSubscriptionId
      ? fetchUpcomingBilling({
          accountId: effectiveAccountId,
          planId: subscription.subscriptionId,
          organizationId: orgId,
          locationId: locId,
        })
      : Promise.resolve(null),
  ]);

  /* Build catalog map */
  const catalogMap = new Map();
  if (catalogRes.status === "fulfilled" && catalogRes.value) {
    const items = extractAddonArray(catalogRes.value?.data);
    items.forEach((item) => {
      if (item?.addonId) catalogMap.set(item.addonId, item);
    });
  }

  /* Step 3 — merge catalog into addons */
  const addons = rawAddons.map((addon) => {
    if (!addon) return addon;
    const detail = catalogMap.get(addon?.addonId);
    if (!detail) return addon;
    return {
      ...addon,
      packageName: addonName(detail) || addon?.packageName || addon?.addonId,
      shortDescription: detail?.shortDescription || detail?.description || addon?.shortDescription,
      category: detail?.category || addon?.category,
    };
  });

  /* Parse upcoming billing */
  let upcomingBilling = null;
  if (billingRes.status === "fulfilled" && billingRes.value) {
    const arr = billingRes.value?.data?.data?.data;
    if (Array.isArray(arr) && arr.length > 0) upcomingBilling = arr[0];
  }

  return {
    subscription,
    upcomingBilling,
    addons,
    fetchedAt: Date.now(),
    subscriptionMessage,
    fetchFailed,
  };
}

/* ──────────────────────────────────────────────────────────
   resolveContext — read accountId/orgId/locId from localStorage
   ────────────────────────────────────────────────────────── */

/**
 * Reads auth/location context from localStorage, handling all login paths.
 * Returns { orgId, locId, orgName, locName, accountId }.
 */
export function resolveContext() {
  const tryParse = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || "null");
    } catch {
      return null;
    }
  };

  const retailUser = tryParse("retailUserDetails") || {};
  const userDetails = tryParse("user_details") || {};

  const orgId = localStorage.getItem("orgId") || "";
  const locId = localStorage.getItem("locId") || "";
  const orgName = localStorage.getItem("orgName") || localStorage.getItem("selectedOrgName") || "";
  const locName =
    localStorage.getItem("LocName") || localStorage.getItem("selectedLocationName") || "";

  // accountId: try explicit key first, then embedded in user objects,
  // then fall back to orgId (retailId e.g. RET_* doubles as accountId in this env)
  const accountId =
    localStorage.getItem("accountId") || retailUser?.accountId || userDetails?.accountId || orgId;

  return { orgId, locId, orgName, locName, accountId };
}
